/**
 * QL-357 — the dashboard widget registry and the pure rules around it.
 *
 * Every widget either dashboard can show is listed here once: which dashboards
 * it can be switched on for, its zone and fixed size, the permission it needs
 * and whether it has a period switch. The API resolves and gates widgets with
 * these helpers, and the web app places them with the same arrangement the
 * Settings layout preview draws, so the preview can never disagree with the
 * dashboard.
 */

import type {
  DashboardArrangement,
  DashboardGridCell,
  DashboardGridRow,
  DashboardKind,
  DashboardLayoutConfig,
  DashboardWidgetDefinition,
  DashboardWidgetKey,
  DashboardWidgetSize,
} from "./types/dashboard";
import { DASHBOARD_WIDGET_KEYS } from "./types/dashboard";

const BOTH: readonly DashboardKind[] = ["user", "performance"];
const USER: readonly DashboardKind[] = ["user"];
const PERFORMANCE: readonly DashboardKind[] = ["performance"];

function keyFigure(key: DashboardWidgetKey): DashboardWidgetDefinition {
  return {
    key,
    zone: "key_figure",
    size: "1/4",
    dashboards: BOTH,
    requiredPermission: null,
    hasPeriod: false,
  };
}

function section(
  key: DashboardWidgetKey,
  size: DashboardWidgetSize,
  dashboards: readonly DashboardKind[],
  options: { compliance?: boolean; hasPeriod?: boolean } = {},
): DashboardWidgetDefinition {
  return {
    key,
    zone: "section",
    size,
    dashboards,
    requiredPermission: options.compliance ? "compliance" : null,
    hasPeriod: options.hasPeriod ?? false,
  };
}

/** Every widget, in the order the Settings lists show them. */
export const DASHBOARD_WIDGETS: readonly DashboardWidgetDefinition[] = [
  keyFigure("clients"),
  keyFigure("total_aum"),
  keyFigure("saving_inflow"),
  keyFigure("activities"),
  keyFigure("flows"),
  keyFigure("completed_this_month"),
  keyFigure("pending_signatures"),
  section("users", "full", PERFORMANCE, { hasPeriod: true }),
  section("pipeline", "2/3", BOTH, { hasPeriod: true }),
  section("next_meetings", "1/3", USER),
  section("action_items", "1/3", BOTH),
  section("recent_activity", "1/3", BOTH),
  section("signatures_expiring", "1/3", PERFORMANCE),
  section("cash_to_invest", "2/3", USER),
  section("approvals_waiting", "1/3", BOTH, { compliance: true }),
  section("review_status", "2/3", BOTH, { compliance: true, hasPeriod: true }),
  section("advice_to_check", "2/3", BOTH, { compliance: true, hasPeriod: true }),
  section("screening", "1/3", BOTH, { compliance: true }),
  section("verified_id", "1/3", BOTH, { compliance: true }),
  section("identity_changes", "1/3", BOTH, { compliance: true }),
];

const BY_KEY = new Map(DASHBOARD_WIDGETS.map((w) => [w.key, w]));

/**
 * What a dashboard shows until a designer sets it up: today's dashboard, the
 * four figures plus action items and recent activity.
 */
export const DEFAULT_DASHBOARD_WIDGETS: Record<DashboardKind, readonly DashboardWidgetKey[]> = {
  user: [
    "clients",
    "flows",
    "completed_this_month",
    "pending_signatures",
    "action_items",
    "recent_activity",
  ],
  performance: [
    "clients",
    "flows",
    "completed_this_month",
    "pending_signatures",
    "action_items",
    "recent_activity",
  ],
};

/** Cash to invest's threshold until a designer changes it, in the tenant currency. */
export const DEFAULT_CASH_THRESHOLD = 1_000_000;

/** Columns in the section grid. */
export const DASHBOARD_SECTION_COLUMNS = 3;

const SECTION_SPAN: Record<DashboardWidgetSize, number> = {
  "1/4": 1,
  "1/3": 1,
  "2/3": 2,
  full: DASHBOARD_SECTION_COLUMNS,
};

export function isDashboardWidgetKey(value: unknown): value is DashboardWidgetKey {
  return typeof value === "string" && (DASHBOARD_WIDGET_KEYS as readonly string[]).includes(value);
}

export function getDashboardWidget(key: DashboardWidgetKey): DashboardWidgetDefinition {
  const widget = BY_KEY.get(key);
  if (!widget) throw new Error(`Unknown dashboard widget: ${key}`);
  return widget;
}

/** The widgets a dashboard can switch on, in registry order. */
export function dashboardWidgetsFor(kind: DashboardKind): DashboardWidgetDefinition[] {
  return DASHBOARD_WIDGETS.filter((w) => w.dashboards.includes(kind));
}

export function isDashboardWidgetAllowed(key: DashboardWidgetKey, kind: DashboardKind): boolean {
  return BY_KEY.get(key)?.dashboards.includes(kind) ?? false;
}

/** Whether a user with these permissions sees (and may compute) the widget. */
export function canSeeDashboardWidget(
  key: DashboardWidgetKey,
  permissions: readonly string[],
): boolean {
  const required = BY_KEY.get(key)?.requiredPermission;
  if (required === undefined) return false;
  return required === null || permissions.includes(required);
}

/**
 * The switched-on widgets of a dashboard in order: the designer's list, or
 * today's dashboard when there is none. Unknown keys, keys not allowed on this
 * dashboard and repeats are dropped, so a stale stored list never breaks the
 * page.
 */
export function resolveDashboardWidgets(
  config: DashboardLayoutConfig | null | undefined,
  kind: DashboardKind,
): DashboardWidgetKey[] {
  const configured = config?.[kind];
  const keys = configured ?? DEFAULT_DASHBOARD_WIDGETS[kind];
  const seen = new Set<DashboardWidgetKey>();
  const result: DashboardWidgetKey[] = [];
  for (const key of keys) {
    if (!isDashboardWidgetKey(key) || seen.has(key) || !isDashboardWidgetAllowed(key, kind)) {
      continue;
    }
    seen.add(key);
    result.push(key);
  }
  return result;
}

export function resolveCashThreshold(config: DashboardLayoutConfig | null | undefined): number {
  const value = config?.cashThreshold;
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : DEFAULT_CASH_THRESHOLD;
}

/**
 * Lay the widgets out: key figures in order on top, sections in a 3-column
 * grid. A section that does not fit the rest of its row starts the next row,
 * and the gap it leaves stays empty — widgets are never moved up to fill it,
 * so the dashboard keeps the designer's order.
 */
export function arrangeDashboard(keys: readonly DashboardWidgetKey[]): DashboardArrangement {
  const keyFigures: DashboardWidgetKey[] = [];
  const sectionRows: DashboardGridRow[] = [];
  let cells: DashboardGridCell[] = [];
  let column = 0;

  const closeRow = () => {
    if (cells.length === 0) return;
    sectionRows.push({ cells, emptyColumns: DASHBOARD_SECTION_COLUMNS - column });
    cells = [];
    column = 0;
  };

  for (const key of keys) {
    const widget = BY_KEY.get(key);
    if (!widget) continue;
    if (widget.zone === "key_figure") {
      keyFigures.push(key);
      continue;
    }
    const span = SECTION_SPAN[widget.size];
    if (column + span > DASHBOARD_SECTION_COLUMNS) closeRow();
    cells.push({ key, column, span });
    column += span;
    if (column === DASHBOARD_SECTION_COLUMNS) closeRow();
  }
  closeRow();

  return { keyFigures, sectionRows };
}
